import {
  InputLabel,
  MenuItem,
  Select,
  Theme,
  Button,
  FormControl,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Spacer } from "@internal/plugin-utilities";
import { alertApiRef, useApi } from "@backstage/core-plugin-api";
import { opsReviewApiRef } from "../../api/OpsReviewApi";
import { getOpsreviewDayOfWeek } from "@internal/plugin-ops-review-common";
import { WinsSection } from "./WinsSection";
import { IssuesAndRecommendationsSection } from "./IssuesAndRecommendationsSection";
import { Progress } from "@backstage/core-components";
import { FreeformSection } from "./FreeformSection";

const useStyles = makeStyles((_theme: Theme) => ({
  label: {
    marginBottom: "10px",
  },
  card: {
    padding: "10px",
  },
  submit: {
    float: "right",
  },
  select: {
    padding: "10px",
  },
}));

interface Props {}

export const OpsReviewEditForm: React.FC<Props> = (): JSX.Element => {
  const classes = useStyles();

  const opsreviewApi = useApi(opsReviewApiRef);
  const alertApi = useApi(alertApiRef);

  const submitRef = useRef<any>(null);

  const [date, setDate] = useState<string>("");
  const [wins, setWins] = useState<string[]>([]);
  const [issuesAndRecommendations, setIssuesAndRecommendations] = useState<
    {
      value: string;
      user: string;
    }[]
  >([]);

  const [freeform, setFreeform] = useState<any[]>([]);

  const opsreviewDayOfWeek = getOpsreviewDayOfWeek();

  const week1 = opsreviewDayOfWeek.format("MM-DD-YYYY");
  const week2 = opsreviewDayOfWeek.add(1, "weeks").format("MM-DD-YYYY");
  const week3 = opsreviewDayOfWeek.add(1, "weeks").format("MM-DD-YYYY");
  const week4 = opsreviewDayOfWeek.add(1, "weeks").format("MM-DD-YYYY");

  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState<boolean>();
  const [submitLoading, setSubmitLoading] = useState<boolean>();

  useEffect(() => {
    if (date !== "") {
      (async () => {
        setData((await opsreviewApi.getOpsReviewDetails(date)).data);
        setLoading(false);
      })();
    }
  }, [date, setData, setLoading, opsreviewApi]);

  useEffect(() => {
    if (data) {
      setLoading(false);
    }
  }, [data]);

  // When changing the date load all the current opsreview detail data
  const handleDateSelectChange = async (
    event: ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    setLoading(true);
    setDate(event.target.value as string);
  };

  const handleSubmit = async (event: any) => {
    setSubmitLoading(true);
    try {
      event.preventDefault();
      await opsreviewApi.setOpsReviewDetails({
        date,
        wins: wins.filter((w) => w),
        issuesAndRecommendations: issuesAndRecommendations.filter((iar) => iar),
        freeform: freeform
          .map((ff) => {
            const content = ff.current.getContent();
            return content;
          })
          .filter((ff) => ff),
      });
      alertApi.post({
        message: `Updated the ${date} OpsReview template`,
        severity: "success",
      });
      // Refresh the view page
      window.location.replace("/ops-review/view");
      setSubmitLoading(false);
    } catch (err: any) {
      alertApi.post({
        message: `Error when attempting to add to the OpsReview: ${err.message}`,
        severity: "error",
      });
      setSubmitLoading(false);
    }
  };

  return (
    <form id="ops-review-form">
      <InputLabel className={classes.label}>
        Select the date for the OpsReview agenda you'd like to edit
      </InputLabel>

      <FormControl fullWidth>
        <InputLabel>Select an OpsReview Date</InputLabel>
        <Select
          fullWidth
          placeholder="Select an OpsReview date"
          id="ops-review-form-date-select"
          value={date}
          onChange={handleDateSelectChange}
          className={classes.select}
        >
          <MenuItem value={week1} key="week1">
            {week1}
          </MenuItem>
          <MenuItem value={week2} key="week2">
            {week2}
          </MenuItem>
          <MenuItem value={week3} key="week3">
            {week3}
          </MenuItem>
          <MenuItem value={week4} key="week4">
            {week4}
          </MenuItem>
        </Select>
      </FormControl>
      <Spacer variant="double" />
      {date && (
        <>
          {loading ? (
            <Progress />
          ) : (
            <WinsSection data={data} setUpstreamData={setWins} />
          )}
          <Spacer variant="double" />
          {loading ? (
            <Progress />
          ) : (
            <IssuesAndRecommendationsSection
              data={data}
              setUpstreamData={setIssuesAndRecommendations}
            />
          )}
          <Spacer variant="double" />
          {loading ? (
            <Progress />
          ) : (
            <FreeformSection
              data={data}
              setUpstreamData={setFreeform}
              submitRef={submitRef}
            />
          )}
          <Button
            ref={submitRef}
            type="submit"
            onClick={handleSubmit}
            variant="outlined"
            className={classes.submit}
            disabled={submitLoading}
          >
            {submitLoading ? <CircularProgress /> : "Submit"}
          </Button>
        </>
      )}
    </form>
  );
};
